import React, { useState, useEffect, useCallback } from 'react';
import { Bell, CheckCircle2, Clock, Briefcase, User, ChevronLeft, ChevronRight, AlertTriangle, Check, RotateCcw, Plus } from 'lucide-react';
import reminderService from '../services/reminderService';
import useTechnicianFilter from '../hooks/useTechnicianFilter';
import TechnicianFilter from './TechnicianFilter';
import useMobileHeaderActions from '../hooks/useMobileHeaderActions';
import { VIEWS } from '../App/utils/appConstants';
import { useToast } from './Toast';

const ordinalSuffix = (d) => {
  if (d > 3 && d < 21) return 'th';
  switch (d % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatDateOrdinal = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = d.getDate();
  const month = d.toLocaleDateString('en-GB', { month: 'long' });
  const year = d.getFullYear();
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day}${ordinalSuffix(day)} ${month} ${year}, ${time}`;
};

const isOverdue = (reminder) => {
  if (reminder.reminder_status !== 'active') return false;
  return new Date(reminder.reminder_datetime) <= new Date();
};

const ReminderList = ({ userProfile, user, onNavigate, onAddJob, onAddContract }) => {
  const toast = useToast();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overdue');
  const [counts, setCounts] = useState({ total: 0, overdue: 0, upcoming: 0, done: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Shared technician filter
  const techFilter = useTechnicianFilter(userProfile);
  const franchiseeId = techFilter.showFilter ? techFilter.getFilteredFranchiseeId() : (userProfile?.franchiseeId || userProfile?.id);
  const branchId = techFilter.getSelectedBranchId();

  useMobileHeaderActions(
    techFilter.showFilter ? <TechnicianFilter {...techFilter} compact /> : null,
    [techFilter.showFilter, techFilter.selectedTechnician, techFilter.techGroups]
  );

  const loadCounts = useCallback(async () => {
    if (!franchiseeId) return;
    try {
      const data = await reminderService.getReminderCounts(franchiseeId, { branch_id: branchId });
      setCounts(data);
    } catch (error) {
      console.error('Error loading reminder counts:', error);
    }
  }, [franchiseeId, branchId]);

  const loadReminders = useCallback(async () => {
    if (!franchiseeId) return;
    setLoading(true);
    try {
      const params = {
        status: tab,
        page,
        limit: 50,
        sortBy: 'reminder_datetime',
        sortOrder: tab === 'done' ? 'DESC' : 'ASC',
        ...(branchId ? { branch_id: branchId } : {})
      };
      const data = await reminderService.getReminders(franchiseeId, params);
      setReminders(data.reminders || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error loading reminders:', error);
    } finally {
      setLoading(false);
    }
  }, [franchiseeId, tab, page, branchId]);

  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { setPage(1); }, [tab, techFilter.selectedTechnician]);
  useEffect(() => { loadReminders(); }, [loadReminders]);

  const handleToggle = async (reminder) => {
    const newStatus = reminder.reminder_status === 'active' ? 'done' : 'active';
    try {
      await reminderService.toggleReminderStatus(franchiseeId, reminder.id, newStatus);
      toast.success(newStatus === 'done' ? 'Reminder completed' : 'Reminder reactivated');
      loadReminders();
      loadCounts();
    } catch (error) {
      console.error('Error toggling reminder:', error);
      toast.error('Failed to update reminder');
    }
  };

  const tabs = [
    { id: 'overdue', label: 'Overdue', count: counts.overdue },
    { id: 'upcoming', label: 'Upcoming', count: counts.upcoming },
    { id: 'done', label: 'Completed', count: counts.done },
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-2 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Bell size={22} className="text-pest-blue" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Reminders</h1>
              <p className="text-sm text-gray-500 hidden sm:block">Track and manage job reminders</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {onAddJob && (
              <button onClick={onAddJob} className="btn btn-md btn-secondary">
                <Plus size={16} />
                <span>Add Job</span>
              </button>
            )}
            {onAddContract && (
              <button onClick={onAddContract} className="btn btn-md btn-secondary">
                <Plus size={16} />
                <span>Add Contract</span>
              </button>
            )}
            <TechnicianFilter {...techFilter} />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span>{t.label}</span>
              {t.count > 0 && (
                <span className={`w-5 h-5 rounded-full text-[10px] inline-flex items-center justify-center font-bold ${
                  t.id === 'overdue' ? 'bg-red-500 text-white' :
                  t.id === 'upcoming' ? 'bg-blue-500 text-white' :
                  'bg-gray-300 text-gray-700'
                }`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-2 sm:px-6 py-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pest-blue mx-auto"></div>
            <p className="text-gray-400 mt-3">Loading reminders...</p>
          </div>
        ) : reminders.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
            <Bell size={32} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No {tab} reminders</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reminders.map(reminder => {
              const overdue = isOverdue(reminder);
              const isDone = reminder.reminder_status === 'done';
              const navigateToJob = () => {
                if (reminder.job_id) {
                  onNavigate(VIEWS.JOB_DETAILS, { id: reminder.job_id, customer_id: reminder.customer_id });
                }
              };
              return (
                <div
                  key={reminder.id}
                  onClick={navigateToJob}
                  className={`bg-white rounded-lg border-2 p-3 sm:p-4 transition-all cursor-pointer hover:shadow-md ${
                    overdue ? 'border-red-300 hover:border-red-400' :
                    isDone ? 'border-gray-200 hover:border-gray-300' :
                    'border-gray-200 hover:border-pest-blue/40'
                  }`}
                >
                  {/* Desktop layout */}
                  <div className="hidden sm:flex items-start gap-4">
                    {/* Date/time column */}
                    <div className="flex-shrink-0 text-center min-w-[80px]">
                      <div className="text-sm font-bold text-gray-900">
                        {new Date(reminder.reminder_datetime).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {new Date(reminder.reminder_datetime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {overdue && (
                        <div className="flex items-center justify-center gap-1 text-red-600 text-xs font-semibold mt-1">
                          <AlertTriangle size={13} /> Overdue
                        </div>
                      )}
                      {isDone && (
                        <div className="flex items-center justify-center gap-0.5 text-green-600 text-[10px] font-medium mt-1">
                          <CheckCircle2 size={10} /> Done
                        </div>
                      )}
                    </div>

                    {/* Divider */}
                    <div className="w-px h-16 bg-gray-200 flex-shrink-0 self-center" />

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-gray-900 ${isDone ? 'line-through text-gray-500' : ''}`}>
                        {reminder.note_text}
                      </p>
                      {reminder.job_title && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-600 mt-0.5">
                          <Briefcase size={13} className="flex-shrink-0" />
                          <span className="truncate">{reminder.job_title}</span>
                        </div>
                      )}
                      {reminder.customer_name && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
                          <User size={13} className="flex-shrink-0" />
                          <span className="truncate">{reminder.customer_name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        {techFilter.showFilter && reminder.author_name && (
                          <span>by {reminder.author_name}</span>
                        )}
                      </div>
                    </div>

                    {/* Action button */}
                    <div className="flex-shrink-0 self-center">
                      {isDone ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggle(reminder); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                        >
                          <RotateCcw size={13} />
                          Reactivate
                        </button>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggle(reminder); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm"
                        >
                          <Check size={13} />
                          Mark Complete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Mobile layout */}
                  <div className="sm:hidden">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <p className={`font-semibold text-gray-900 text-sm flex-1 ${isDone ? 'line-through text-gray-500' : ''}`}>
                        {reminder.note_text}
                      </p>
                      {overdue && (
                        <span className="flex items-center gap-1 text-red-600 text-xs font-semibold flex-shrink-0 mt-0.5">
                          <AlertTriangle size={13} /> Overdue
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mb-2.5">
                      <span className="flex items-center gap-1">
                        <Clock size={11} />
                        {formatDateOrdinal(reminder.reminder_datetime)}
                      </span>
                      {reminder.job_title && (
                        <span className="flex items-center gap-1">
                          <Briefcase size={11} />
                          {reminder.job_title}
                        </span>
                      )}
                      {reminder.customer_name && (
                        <span className="flex items-center gap-1">
                          <User size={11} />
                          {reminder.customer_name}
                        </span>
                      )}
                    </div>
                    {isDone ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggle(reminder); }}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <RotateCcw size={13} />
                        Reactivate
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggle(reminder); }}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm"
                      >
                        <Check size={13} />
                        Mark Complete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn btn-sm btn-outline disabled:opacity-50"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="btn btn-sm btn-outline disabled:opacity-50"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReminderList;
